import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

import { TechnicianItemService } from '../../../core/services/technician-item.service';
import { TechnicianService } from '../../../core/services/technician.service';
import { AuthService } from '../../../core/services/auth.service';
import { TechnicianInventoryLine } from '../../../core/models/technician-item.model';
import { Technician } from '../../../core/models/technician.model';

interface InventoryGroup {
  category: string;
  items: TechnicianInventoryLine[];
}

@Component({
  selector: 'app-technician-inventory',
  imports: [CommonModule, RouterLink],
  templateUrl: './technician-inventory.html',
  styleUrl: './technician-inventory.scss',
})
export class TechnicianInventory {
  private readonly technicianItemService = inject(TechnicianItemService);
  private readonly technicianService = inject(TechnicianService);
  protected readonly authService = inject(AuthService);

  protected readonly technicians = signal<Technician[]>([]);
  protected readonly selectedTechnicianId = signal<number | null>(null);

  protected readonly matchedOwnTechnician = signal(false);
  protected readonly ownTechnicianName = signal<string | null>(null);

  protected readonly items = signal<TechnicianInventoryLine[]>([]);
  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly saveMessage = signal<string | null>(null);

  protected readonly mode = signal<'view' | 'edit'>('view');

  private groupByCategory(items: TechnicianInventoryLine[]): InventoryGroup[] {
    const groups = new Map<string, TechnicianInventoryLine[]>();
    for (const item of items) {
      const category = item.category ?? 'Other';
      const list = groups.get(category) ?? [];
      list.push(item);
      groups.set(category, list);
    }
    return Array.from(groups.entries()).map(([category, items]) => ({ category, items }));
  }

  protected readonly viewGroups = computed(() => this.groupByCategory(this.items().filter((i) => i.quantity > 0)));
  protected readonly editGroups = computed(() => this.groupByCategory(this.items()));
  protected readonly hasAnyItems = computed(() => this.items().some((i) => i.quantity > 0));

  protected readonly canEditSelected = computed(() => this.matchedOwnTechnician() || this.authService.isAdmin());

  constructor() {
    this.technicianService.getTechnicians({ pageSize: 200 }).subscribe({
      next: (r) => {
        const active = r.items.filter((t) => t.isActive);
        this.technicians.set(active);

        const signedInEmail = (this.authService.account()?.username ?? '').toLowerCase();
        const own = signedInEmail ? active.find((t) => (t.email ?? '').toLowerCase() === signedInEmail) : undefined;
        if (own) {
          this.matchedOwnTechnician.set(true);
          this.ownTechnicianName.set(`${own.firstName ?? ''} ${own.lastName ?? ''}`.trim() || own.email);
          this.selectedTechnicianId.set(own.technicianId);
          this.loadInventory();
        }
      }
    });
  }

  protected onTechnicianChange(value: string): void {
    const newId = value ? Number(value) : null;
    this.selectedTechnicianId.set(newId);

    const signedInEmail = (this.authService.account()?.username ?? '').toLowerCase();
    const selectedTech = this.technicians().find((t) => t.technicianId === newId);
    this.matchedOwnTechnician.set(!!selectedTech && (selectedTech.email ?? '').toLowerCase() === signedInEmail);

    this.loadInventory();
  }

  private loadInventory(): void {
    const technicianId = this.selectedTechnicianId();
    this.mode.set('view');
    if (!technicianId) {
      this.items.set([]);
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.saveMessage.set(null);
    this.technicianItemService.getTechnicianInventory(technicianId).subscribe({
      next: (result) => {
        this.items.set(result.items);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load inventory. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  protected startEdit(): void {
    this.saveMessage.set(null);
    this.mode.set('edit');
  }

  protected cancelEdit(): void {
    this.loadInventory();
  }

  protected updateQuantity(itemId: number, quantity: string): void {
    const value = Math.max(0, Number(quantity) || 0);
    this.items.update((list) => list.map((i) => (i.itemId === itemId ? { ...i, quantity: value } : i)));
  }

  protected save(): void {
    const technicianId = this.selectedTechnicianId();
    if (!technicianId) return;

    this.saving.set(true);
    this.error.set(null);
    this.saveMessage.set(null);
    this.technicianItemService.updateTechnicianInventory(technicianId, {
      items: this.items().map((i) => ({ itemId: i.itemId, quantity: i.quantity }))
    }).subscribe({
      next: (result) => {
        this.items.set(result.items);
        this.saving.set(false);
        this.saveMessage.set('Inventory saved.');
        this.mode.set('view');
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set('Failed to save inventory.');
        console.error(err);
      }
    });
  }
}
